import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useDebounce, useMount, useMountedState, useUnmount } from 'react-use';
import { GeocodingFeature } from '../../../common/types/mapbox-search-api-v1.types';
import { parseCoordinates } from '../../../common/utils/helpers/geo';

import {
  suggestionsChanged,
  focusedChanged,
} from '../../../common/store/search';
import { selectCenter } from '../../../map/store';
import { useApiClient } from '../../../common/contexts/api-context';
import {
  compilePath,
  mergeQueries,
} from '../../../common/utils/helpers/routes';

import SearchField from './search-field/SearchField';

const SEARCH_QUERY_MINIMAL_LENGTH = 2;
const SEARCH_TYPING_DEBOUNCE_INTERVAL = 300;

// 1s is required for safari with bottom tabs in ios 15 to input unfocus after character typing into search input
const SEARCH_LOCATION_SYNC_DEBOUNCE_INTERVAL = 1000;

const Search = (props) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const viewportCenter = useSelector(selectCenter);

  useMount(() => {
    dispatch(focusedChanged(false));
  });

  const urlSearchValue = new URLSearchParams(location.search).get('q') || '';
  const [searchValue, setSearchValue] = useState(urlSearchValue);

  const handleSearch = useCallback(() => {
    history.push({
      pathname: compilePath('results-list'),
      search: history.location.search,
    });
  }, [history]);

  const dispatchSuggestionsChanged = useCallback(
    (params, features) => {
      dispatch(suggestionsChanged({ params, features }));
    },
    [dispatch],
  );

  const handleClear = useCallback(() => {
    dispatchSuggestionsChanged('', null);
    history.push({
      pathname: compilePath('root'),
      search: mergeQueries(history.location.search, { q: null }),
    });
  }, [history, dispatchSuggestionsChanged]);

  const handleFocus = useCallback(
    () => dispatch(focusedChanged(true)),
    [dispatch],
  );

  const handleBlur = useCallback(
    () => dispatch(focusedChanged(false)),
    [dispatch],
  );

  const handleBack = useCallback(() => history.goBack(), [history]);
  useEffect(() => setSearchValue(urlSearchValue), [urlSearchValue]);

  const handleChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  const api = useApiClient();
  const isMounted = useMountedState();

  useDebounce(
    async () => {
      const params = { query: searchValue, proximity: viewportCenter };
      if (searchValue.length < SEARCH_QUERY_MINIMAL_LENGTH) {
        dispatchSuggestionsChanged(params, null);
      } else {
        let features: GeocodingFeature[] = [];

        const coordinates = parseCoordinates(params.query);

        if (coordinates !== null) {
          const [lng, lat] = coordinates;
          features = await api.reverseSearch({
            lat,
            lng,
          });
        } else {
          features = await api.forwardSearch(params);
        }

        if (isMounted()) {
          dispatchSuggestionsChanged(params, features);
        }
      }
    },
    SEARCH_TYPING_DEBOUNCE_INTERVAL,
    [searchValue], // `viewportCenter` is intentionally omitted from deps array
  );

  useDebounce(
    () => {
      history.replace({
        ...history.location,
        search: mergeQueries(history.location.search, { q: searchValue }),
      });
    },
    SEARCH_LOCATION_SYNC_DEBOUNCE_INTERVAL,
    [history, searchValue],
  );

  useUnmount(() => {
    dispatch(
      suggestionsChanged({
        params: { query: '' },
        features: null,
      }),
    );
  });

  return (
    <SearchField
      {...props}
      value={searchValue}
      onChange={handleChange}
      onSearch={handleSearch}
      onClear={handleClear}
      onBack={handleBack}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};

export default Search;
